'use client';

import { FormEvent, useState } from 'react';
import TurnstileClient from '@/components/TurnstileClient';
import Card from './Card';

interface JobApplicationFormProps {
    jobId: string;
    jobTitle?: string;
    siteKey?: string;
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export default function JobApplicationForm({ jobId, jobTitle, siteKey }: JobApplicationFormProps) {
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    async function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSubmitState('submitting');
        setErrorMessage('');

        const form = event.currentTarget;
        const formData = new FormData(form);
        const turnstileToken = String(formData.get('cf-turnstile-response') || '');

        if (!turnstileToken) {
            setSubmitState('error');
            setErrorMessage('Bitte bestätige zuerst die Sicherheitsabfrage.');
            return;
        }

        const payload = {
            firstName: String(formData.get('firstName') || ''),
            lastName: String(formData.get('lastName') || ''),
            email: String(formData.get('email') || ''),
            phone: String(formData.get('phone') || ''),
            linkedin: String(formData.get('linkedin') || ''),
            message: String(formData.get('message') || ''),
            confirm: formData.get('confirm') === 'on',
            website: String(formData.get('website') || ''),
            turnstileToken,
            jobId,
        };

        try {
            const response = await fetch('/api/jobs/apply', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = (await response.json()) as { success?: boolean; error?: string };

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Bewerbung konnte nicht gesendet werden.');
            }

            setSubmitState('success');
            form.reset();
        } catch (error) {
            setSubmitState('error');
            setErrorMessage(error instanceof Error ? error.message : 'Unbekannter Fehler beim Senden.');
        }
    }

    return (
        <section className="max-w-4xl mx-auto mt-32">
            <Card>
                <h2 className="text-2xl text-center font-semibold text-primary">Jetzt bewerben</h2>
                {jobTitle && (
                    <h3 className="text-xl! text-center mt-2">Position: {jobTitle}</h3>
                )}

                <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="job-firstName" className="block text-sm font-medium text-white mb-1">
                                Vorname*
                            </label>
                            <input
                                id="job-firstName"
                                name="firstName"
                                type="text"
                                placeholder="Vorname"
                                required
                                maxLength={100}
                                className="mt-1 py-3 px-4 bg-neutral-700/50 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                            />
                        </div>
                        <div>
                            <label htmlFor="job-lastName" className="block text-sm font-medium text-white mb-1">
                                Nachname*
                            </label>
                            <input
                                id="job-lastName"
                                name="lastName"
                                type="text"
                                placeholder="Nachname"
                                required
                                maxLength={100}
                                className="mt-1 py-3 px-4 bg-neutral-700/50 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="job-email" className="block text-sm font-medium text-white mb-1">
                                E-Mail*
                            </label>
                            <input
                                id="job-email"
                                name="email"
                                type="email"
                                placeholder="name@beispiel.de"
                                required
                                className="mt-1 py-3 px-4 bg-neutral-700/50 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                            />
                        </div>
                        <div>
                            <label htmlFor="job-phone" className="block text-sm font-medium text-white mb-1">
                                Telefon
                            </label>
                            <input
                                id="job-phone"
                                name="phone"
                                type="tel"
                                placeholder="+49 ..."
                                maxLength={40}
                                className="mt-1 py-3 px-4 bg-neutral-700/50 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="job-linkedin" className="block text-sm font-medium text-white mb-1">
                            LinkedIn
                        </label>
                        <input
                            id="job-linkedin"
                            name="linkedin"
                            type="url"
                            placeholder="https://linkedin.com/in/..."
                            maxLength={500}
                            className="mt-1 py-3 px-4 bg-neutral-700/50 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="job-message" className="block text-sm font-medium text-white mb-1">
                            Nachricht
                        </label>
                        <textarea
                            id="job-message"
                            name="message"
                            placeholder="Nachricht (optional)"
                            maxLength={10000}
                            rows={6}
                            className="mt-1 py-3 px-4 bg-neutral-700/50 text-white block w-full rounded-lg border border-neutral-800 shadow-sm focus:ring-blue-500 sm:text-sm"
                        />
                    </div>

                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        className="hidden"
                        aria-hidden="true"
                    />

                    <label className="flex items-start gap-3 text-sm text-primary">
                        <input type="checkbox" name="confirm" required className="mt-1" />
                        <span>Ich bestätige die Richtigkeit meiner Angaben und stimme der Verarbeitung meiner Daten zu.</span>
                    </label>

                    {siteKey ? (
                        <TurnstileClient siteKey={siteKey} />
                    ) : (
                        <p className="text-sm text-red-700">Turnstile ist noch nicht konfiguriert (NEXT_PUBLIC_CLOUDFLARE_KEY).</p>
                    )}

                    <button
                        type="submit"
                        disabled={submitState === 'submitting'}
                        className="main-button main-button--secondary mt-2 w-full max-w-xs"
                    >
                        <span className="text-white mx-auto">
                            {submitState === 'submitting' ? 'Wird gesendet...' : 'Bewerbung absenden'}
                        </span>
                    </button>

                    {submitState === 'success' && (
                        <p className="text-sm text-green-700">Danke, deine Bewerbung wurde erfolgreich versendet.</p>
                    )}
                    {submitState === 'error' && (
                        <p className="text-sm text-red-700">{errorMessage || 'Senden fehlgeschlagen.'}</p>
                    )}
                </form>
            </Card>
        </section>
    );
}